import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import { Platform, StyleSheet, View } from 'react-native';
import Animated, { FadeInDown, FadeOutDown } from 'react-native-reanimated';

import {
  LoadingAnimation,
  ScreenHeader as GarnishScreenHeader,
  theme,
  useElementVisibility,
  useIsFocused,
  useResponsive,
  webOnlyStyles,
} from '@sg/garnish';
import { ReorderRail } from '@sg/shared-fe-features-menu';

import { useContentfulContentTypeEntry } from '@order/Contentful';
import {
  getBannerContent,
  type HomeContent,
  HomeProductCategory,
  HomeV2,
  useHomeContent,
  useHomeFiltering,
  useHomeLastCanceledOrder,
  useHomeMenu,
  useHomePendingFeedbackOrder,
} from '@order/features-home';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useDeliveryDisclosure } from '@order/shared-hooks';
import { useTelemetry } from '@order/Telemetry';

import { AppFooter, MenuReorderRail, ScreenHeader } from '../../../../components';
import {
  getLastInteractedDeliveryDetails,
  useDietaryPreferencesMachine,
  useLastInteractedStore,
  useLastInteractedStoreMenuHref,
} from '../../../../context';
import { useIsLoggedIn } from '../../../../context/AuthMachine';
import {
  useLastOrderStatus,
  useMenuReorderRailOrders,
  useOneClickAddToBag,
  usePendingOrder,
} from '../../../../hooks';
import { isPendingOrder, isPendingOrderStatus } from '../../utils';

export const HomeScreenContentV2 = () => {
  const isLoggedIn = useIsLoggedIn();
  const isFocused = useIsFocused();
  const router = useRouter();
  const { track } = useTelemetry();
  const { currentBreakpoint: { isXS }, match } = useResponsive(); // prettier-ignore

  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');

  // ─── Styles ────────────────────────────────────────────────────────────────
  // So that the banner takes up 70% of the height of the screen on larger viewports.

  const bannerHeight = match([null, webOnlyStyles({ minHeight: '70svh' as never })]);

  // ─── Menu ──────────────────────────────────────────────────────────────────

  const lastInteractedStore = useLastInteractedStore() ?? {};
  const { restaurantId, restaurantSlug = '', restaurantName, addressId } = lastInteractedStore;
  const deliveryDetails = getLastInteractedDeliveryDetails(lastInteractedStore);
  const menuHref = useLastInteractedStoreMenuHref();

  const { restaurant, products, isLoadingMenu } = useHomeMenu({ restaurantId, addressId });
  const { deliveryDisclosure } = useDeliveryDisclosure({
    restaurantDeliveryFee: restaurant?.deliveryFee,
  });
  const pendingOrder = usePendingOrder({
    requestPolicy: 'cache-and-network',
    withPolling: true,
  });

  // ─── Allergens ─────────────────────────────────────────────────────────────

  const { restrictions: dietaryPreferences } = useDietaryPreferencesMachine();

  // ─── Content ───────────────────────────────────────────────────────────────

  const entry = useContentfulContentTypeEntry<HomeContent>({ contentType: 'homeScreen', include: 2 }); // prettier-ignore
  const content = useHomeContent(entry?.data?.fields);
  const isLoadingContent = entry?.fetching;

  // ─── Filtering ─────────────────────────────────────────────────────────────

  const { filterableCategories, genericCategories } = useHomeFiltering({
    products,
    filterableCategories: content.filterableCategories,
    genericCategories: content.genericCategories,
  });

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const filterableCategoriesRef = useRef<View>(null);
  const genericCategoriesRef = useRef<View>(null);

  // ─── State ─────────────────────────────────────────────────────────────────

  const [selectedCategory, setSelectedCategory] = useState<HomeProductCategory | null>(null);

  // ─── Flags ─────────────────────────────────────────────────────────────────

  const canRenderGenericCategories = genericCategories.length > 0 && !selectedCategory;

  const isFilterableCategoriesVisible = useElementVisibility(filterableCategoriesRef, {
    isEnabled: !isXS && Boolean(selectedCategory),
  });
  const isGenericCategoriesVisible = useElementVisibility(genericCategoriesRef, {
    isEnabled: !isXS && canRenderGenericCategories,
  });

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleCategorySelection = useCallback(
    (category: HomeProductCategory | null) => {
      track('home.filter.selected', { filterName: category?.categoryName ?? null });
      setSelectedCategory(category);
    },
    [track],
  );

  // ─── Reorder ───────────────────────────────────────────────────────────────

  const { orders, mostRecentLineItems, mostRecentOrder, isFetchingMostRecentOrders } =
    useMenuReorderRailOrders({
      restaurantId: Number.isNaN(Number(restaurantId)) ? 0 : Number(restaurantId),
      enabled: isLoggedIn && Boolean(restaurantId),
      requestPolicy: 'network-only',
    });

  // Filtering should only hide the reorder rail on XS.
  const shouldHideReorderRailOnXS = Boolean(selectedCategory) && isXS;

  const canRenderReorderRail =
    !shouldHideReorderRailOnXS &&
    restaurantId &&
    restaurantSlug &&
    restaurantName &&
    mostRecentLineItems.length > 0;

  // ─── Pending Order ───────────────────────────────────────────────────────
  // Loads the last order and its order status from cache (requested on home screen).

  const lastOrderStatus = useLastOrderStatus({ requestPolicy: 'cache-only' });
  const isLoadingLastOrderStatus = lastOrderStatus?.isLoadingLastOrder;
  const hasPendingOrder = isPendingOrderStatus(lastOrderStatus?.orderStatus);

  // ─── Multiple Pending Orders ───────────────────────────────────────────────
  // Extra requirement to show when there are multiple pending orders.

  const pendingOrders = useMemo(() => orders.filter(isPendingOrder), [orders]);
  const hasMultiplePendingOrders = pendingOrders.length > 1;

  // ─── Last Canceled Order ───────────────────────────────────────────────────

  const { lastCanceledOrderId, hasRecentCanceledOrder } = useHomeLastCanceledOrder({ pause: !isLoggedIn || !isFocused }); // prettier-ignore

  // ─── Pending Feedback ──────────────────────────────────────────────────────

  const lastOrderId = orders?.[0]?.id;
  const pendingFeedbackOrder = useHomePendingFeedbackOrder({ orderId: lastOrderId, isLoggedIn });

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const { addProductToBag } = useOneClickAddToBag({
    restaurantSlug,
    deliveryDetails,
  });

  const openMenu = useCallback(() => {
    router.navigate('/menu');
  }, [router]);

  const trackMenuView = useCallback(() => {
    track('home.view_menu.selected');
  }, [track]);

  const openProductDetails = useCallback(
    (slug: string) => {
      if (deliveryDetails?.addressId) {
        router.navigate({
          pathname: '/delivery/[addressId]/[productSlug]',
          params: { addressId: deliveryDetails.addressId, productSlug: slug },
        });
        return;
      }

      router.navigate({
        pathname: '/[restaurantSlug]/[productSlug]',
        params: { restaurantSlug, productSlug: slug },
      });
    },
    [deliveryDetails?.addressId, restaurantSlug, router],
  );

  const handleAddProductToBag = useCallback(
    (id: string, name: string) => {
      return addProductToBag({ id, name }, 1);
    },
    [addProductToBag],
  );

  const viewOrders = useCallback(() => {
    track('home.post_order.view_order.selected');

    if (hasMultiplePendingOrders) {
      router.navigate('/account/orders');
      return;
    }

    if (hasPendingOrder) {
      router.navigate(`/order-status/${lastOrderStatus.orderId}`);
    }
  }, [router, hasMultiplePendingOrders, hasPendingOrder, lastOrderStatus, track]);

  const viewCanceledOrder = useCallback(() => {
    if (!lastCanceledOrderId) return;

    router.navigate(`/order-status/${lastCanceledOrderId}`);
  }, [router, lastCanceledOrderId]);

  const openRewards = useCallback(() => {
    track('home.post_order.points_cta.selected');
    router.navigate('/rewards');
  }, [router, track]);

  const rateOrder = useCallback(() => {
    if (!pendingFeedbackOrder) return;

    track('home.post_order.rate_order.selected');
    router.navigate(`/feedback/${pendingFeedbackOrder.id}`);
  }, [router, pendingFeedbackOrder, track]);

  const postOrderBanner = match([content.postOrderBannerXs, content.postOrderBannerSm]);

  const trackPostOrderBannerCtaPress = useCallback(() => {
    track('home.post_order.campaign.selected', { campaignId: postOrderBanner.id });
  }, [track, postOrderBanner.id]);

  const trackItemSelection = useCallback(
    (product: string, category: string) => {
      track('home.product.selected', { product, category });
    },
    [track],
  );

  const disclaimer =
    addressId && restaurant?.showDeliveryFeeDisclosure ? deliveryDisclosure : undefined;

  const isProductCategoryVisible = isFilterableCategoriesVisible === true || isGenericCategoriesVisible === true; // prettier-ignore

  const shouldShowDisclaimer = Platform.select({
    web: Boolean(disclaimer && isProductCategoryVisible),
    default: Boolean(disclaimer),
  });

  // ───────────────────────────────────────────────────────────────────────────

  const footerStyles = useMemo(() => {
    if (hasRecentCanceledOrder && disclaimer) {
      return styles.footerWithTwoBottomNotices;
    }

    if (hasRecentCanceledOrder || disclaimer) {
      return styles.footerWithOneBottomNotice;
    }
  }, [disclaimer, hasRecentCanceledOrder]);

  // ───────────────────────────────────────────────────────────────────────────

  if (isLoadingContent || isLoadingMenu || isFetchingMostRecentOrders || isLoadingLastOrderStatus) {
    return <LoadingAnimation />;
  }

  if (hasPendingOrder || hasMultiplePendingOrders) {
    const order = lastOrderStatus ?? pendingOrders[0];
    const product = order?.lineItems[0]?.product;
    const cardContent = hasMultiplePendingOrders ? content.postOrderCardMultiple : content.postOrderCard; // prettier-ignore
    const hasEarnedLoyaltyPoints = lastOrderStatus.hasEarnedLoyaltyPoints || pendingOrders.some((order) => order.hasEarnedLoyaltyPoints); // prettier-ignore

    return (
      <>
        {isNavbarV2Enabled && isXS ? (
          <ScreenHeader pendingOrder={pendingOrder} rounded={false} />
        ) : null}

        <HomeV2.Container>
          <HomeV2.PostOrderContent>
            {isXS ? (
              <>
                <HomeV2.OrderCard
                  title={cardContent.title}
                  description={cardContent.description}
                  cta={cardContent.cta}
                  image={product?.asset?.url}
                  imageAlt={product?.name}
                  orderCount={order.lineItems.length}
                  onPress={viewOrders}
                />

                <HomeV2.OrderBanner
                  title={postOrderBanner.title}
                  description={postOrderBanner.description}
                  image={postOrderBanner.image}
                  imageAlt={postOrderBanner.imageA11y}
                  deepLink={postOrderBanner.deepLink}
                  foregroundColor={postOrderBanner.foregroundColor}
                  cta={postOrderBanner.cta}
                  onPress={trackPostOrderBannerCtaPress}
                />

                {hasEarnedLoyaltyPoints ? (
                  <HomeV2.LoyaltyCard
                    title={content.postOrderLoyalty.title}
                    description={content.postOrderLoyalty.description}
                    cta={content.postOrderLoyalty.cta}
                    onPress={openRewards}
                  />
                ) : null}
              </>
            ) : (
              <>
                <HomeV2.OrderBanner.Image
                  image={postOrderBanner.image}
                  imageAlt={postOrderBanner.imageA11y}
                />

                <HomeV2.PostOrderContent.Footer>
                  <HomeV2.OrderBanner
                    title={postOrderBanner.title}
                    description={postOrderBanner.description}
                    deepLink={postOrderBanner.deepLink}
                    foregroundColor={postOrderBanner.foregroundColor}
                    cta={postOrderBanner.cta}
                    onPress={trackPostOrderBannerCtaPress}
                  />

                  <HomeV2.PostOrderContent.Info>
                    <HomeV2.OrderCard
                      title={cardContent.title}
                      description={cardContent.description}
                      cta={cardContent.cta}
                      image={product?.asset?.url}
                      imageAlt={product?.name}
                      orderCount={order.lineItems.length}
                      onPress={viewOrders}
                    />

                    {hasEarnedLoyaltyPoints ? (
                      <HomeV2.LoyaltyCard
                        title={content.postOrderLoyalty.title}
                        description={content.postOrderLoyalty.description}
                        cta={content.postOrderLoyalty.cta}
                        onPress={openRewards}
                      />
                    ) : null}
                  </HomeV2.PostOrderContent.Info>
                </HomeV2.PostOrderContent.Footer>
              </>
            )}
          </HomeV2.PostOrderContent>

          {isXS ? null : <AppFooter />}
        </HomeV2.Container>
      </>
    );
  }

  return (
    <>
      {isNavbarV2Enabled && isXS ? (
        <ScreenHeader disclaimer={disclaimer} pendingOrder={pendingOrder} rounded={false} />
      ) : null}

      <HomeV2.Container stickyHeaderIndices={hasRecentCanceledOrder ? [0] : []}>
        {hasRecentCanceledOrder && isXS ? (
          <HomeV2.OrderCanceledBanner onPress={viewCanceledOrder} />
        ) : null}

        <HomeV2.Container.Content>
          <HomeV2.Banner
            style={bannerHeight}
            size={isLoggedIn ? 'small' : 'large'}
            content={getBannerContent(content, isLoggedIn, isXS)}
          />

          {canRenderReorderRail && !isXS ? (
            <HomeV2.ReorderRail.Container>
              <ReorderRail.Header>
                <HomeV2.ReorderRail.SideWrapper />

                <ReorderRail.Heading />

                <HomeV2.ReorderRail.SideWrapper>
                  <MenuReorderRail.SeeAll
                    restaurantId={restaurantId}
                    deliveryDetails={deliveryDetails}
                  />
                </HomeV2.ReorderRail.SideWrapper>
              </ReorderRail.Header>

              <MenuReorderRail.List
                location="home"
                lineItems={mostRecentLineItems}
                mostRecentOrder={mostRecentOrder}
                restaurantId={restaurantId}
                restaurantName={restaurantName}
                restaurantSlug={restaurantSlug}
                deliveryDetails={deliveryDetails}
              />
            </HomeV2.ReorderRail.Container>
          ) : null}

          {pendingFeedbackOrder && !isXS ? (
            <HomeV2.RateOrderCard
              image={pendingFeedbackOrder.lineItems[0]?.product?.asset?.url}
              orderCount={pendingFeedbackOrder.lineItems.length}
              onPress={rateOrder}
            />
          ) : null}

          {filterableCategories.length > 0 ? (
            <HomeV2.FilterableCategories
              ref={filterableCategoriesRef}
              title={content.filtersTitle}
              filterableCategories={filterableCategories}
              dietaryPreferences={dietaryPreferences}
              selectedCategory={selectedCategory}
              onSelectCategory={handleCategorySelection}
              openMenu={openMenu}
              openProductDetails={openProductDetails}
              addProductToBag={handleAddProductToBag}
              trackItemSelection={trackItemSelection}
            />
          ) : null}

          {canRenderReorderRail && isXS ? (
            <HomeV2.ReorderRail.Container>
              <ReorderRail.Header>
                <ReorderRail.Heading />

                <MenuReorderRail.SeeAll
                  restaurantId={restaurantId}
                  deliveryDetails={deliveryDetails}
                />
              </ReorderRail.Header>

              <MenuReorderRail.List
                disableAnimation
                location="home"
                lineItems={mostRecentLineItems}
                mostRecentOrder={mostRecentOrder}
                restaurantId={restaurantId}
                restaurantName={restaurantName}
                restaurantSlug={restaurantSlug}
                deliveryDetails={deliveryDetails}
              />
            </HomeV2.ReorderRail.Container>
          ) : null}

          {pendingFeedbackOrder && !selectedCategory && isXS ? (
            <HomeV2.RateOrderCard
              image={pendingFeedbackOrder.lineItems[0]?.product?.asset?.url}
              orderCount={pendingFeedbackOrder.lineItems.length}
              onPress={rateOrder}
            />
          ) : null}

          {canRenderGenericCategories ? (
            <HomeV2.Container.AnimatedContent>
              <HomeV2.GenericCategories
                ref={genericCategoriesRef}
                genericCategories={genericCategories}
                dietaryPreferences={dietaryPreferences}
                openMenu={openMenu}
                openProductDetails={openProductDetails}
                addProductToBag={handleAddProductToBag}
                trackItemSelection={trackItemSelection}
              />
            </HomeV2.Container.AnimatedContent>
          ) : null}

          <HomeV2.Footer
            menuHref={menuHref}
            title={content.footerTitle}
            imageUrl={content.footerImageUrl}
            imageA11y={content.footerImageA11y}
            trackMenuView={trackMenuView}
          />
        </HomeV2.Container.Content>

        {isXS ? null : <AppFooter style={footerStyles} />}
      </HomeV2.Container>

      {!isXS ? (
        <View style={styles.bottomBannersContainer}>
          {shouldShowDisclaimer && disclaimer ? (
            <Animated.View
              entering={DISCLAIMER_ENTERING_ANIMATION}
              exiting={DISCLAIMER_EXITING_ANIMATION}
            >
              <GarnishScreenHeader.Disclaimer disclaimer={disclaimer} />
            </Animated.View>
          ) : null}

          {hasRecentCanceledOrder ? (
            <HomeV2.OrderCanceledBanner onPress={viewCanceledOrder} />
          ) : null}
        </View>
      ) : null}
    </>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const DISCLAIMER_ENTERING_ANIMATION = FadeInDown.duration(theme.transitions.short);
const DISCLAIMER_EXITING_ANIMATION = FadeOutDown.duration(theme.transitions.short);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  bottomBannersContainer: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: theme.zIndex.fixed,
  },
  footerWithOneBottomNotice: {
    paddingBottom: theme.spacing['20'],
  },
  footerWithTwoBottomNotices: {
    paddingBottom: theme.spacing['35'],
  },
});
